import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  /** @type {Set<() => void>} */
  #resizeCleanups = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
    if (this.classList.contains('product-description')) {
      this.querySelectorAll(':scope > ul').forEach((ul) => this.#initProductDescriptionPillList(ul));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeCleanups.forEach((fn) => fn());
    this.#resizeCleanups.clear();
  }

  /**
   * Horizontal pill list + edge gradients when the row overflows
   * @param {HTMLUListElement} ul
   */
  #initProductDescriptionPillList(ul) {
    if (ul.closest('.product-description__list-outer')) return;

    const outer = document.createElement('div');
    outer.className = 'product-description__list-outer';
    const scroller = document.createElement('div');
    scroller.className = 'product-description__list-scroller';
    scroller.setAttribute('tabindex', '0');
    scroller.setAttribute('role', 'group');

    const parent = ul.parentNode;
    if (parent) {
      parent.insertBefore(outer, ul);
    }
    scroller.append(ul);
    outer.appendChild(scroller);

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = scroller;
      const overflow = scrollWidth - clientWidth > 1;
      const minScroll = 1;
      const maxScroll = scrollWidth - clientWidth - minScroll;
      outer.classList.toggle('product-description__list-outer--left', overflow && scrollLeft > minScroll);
      outer.classList.toggle('product-description__list-outer--right', overflow && scrollLeft < maxScroll);
    };

    scroller.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(scroller);
    this.#resizeCleanups.add(() => {
      scroller.removeEventListener('scroll', update);
      ro.disconnect();
    });
    requestAnimationFrame(update);
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
